import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "./Modal";
// import { useSelector } from "react-redux";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
// Assuming companySlice selects the necessary license counts correctly
// import { selectAllCompanys } from "../utils/redux/companySlice";
/* import {
  selectAssessments,
} from "../utils/redux/assessmentSlice"; */
import PropTypes from "prop-types";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

// Assuming supportedLanguages is defined elsewhere or keep it here if local
const supportedLanguages = [
    { value: "en", label: "English" },
    { value: "zh", label: "中文 (Mandarin Chinese)" },
    { value: "es", label: "Español (Spanish)" },
    { value: "ar", label: "العربية (Arabic)" },
    { value: "hi", label: "हिन्दी (Hindi)" },
    { value: "pt", label: "Português (Portuguese)" },
    { value: "fr", label: "Français (French)" },
    { value: "de", label: "Deutsch (German)" },
    { value: "ru", label: "Русский (Russian)" },
    { value: "ja", label: "日本語 (Japanese)" },
  ];


const CreateJobModal = ({
  isOpen,
  onClose,
  onSubmit,
  onChange,
  data,
  disableButtons,
  assessments, // Pass assessments data
  // Pass license counts from parent component
  basicLicenseCount,
  growthLicenseCount,
  proLicenseCount,   // Assuming 'pro' maps to Professional
  premiumLicenseCount,
  hasUsedFreeTrial, // Pass trial status
}) => {
  const { t } = useTranslation("createJobModal");
  // Default to 'pro' (Professional) as it's marked "Most Popular"
  const [jobOfferType, setJobOfferType] = useState("pro");
  // Removed useSelector for companyData, assuming license counts are passed via props
  // const companyData = useSelector(selectAllCompanys);
  const [usingFreeTrial, setUsingFreeTrial] = useState(false);
  const [showForm, setShowForm] = useState(false); // State to control form visibility
  // Removed useSelector for assessments, assuming it's passed via props
  // const assessments = useSelector(selectAssessments);
  // eslint-disable-next-line no-unused-vars
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [showTooltip, setShowTooltip] = useState(null);

  // Effect to set default language (remains unchanged, assuming parent still provides company data if needed for language)
  useEffect(() => {
    const browserLang = navigator.language.split("-")[0];
    // You might need a different way to get default language if companyData isn't available here
    // For now, let's default to browser or English
    const defaultLang = supportedLanguages.find(
      (lang) => lang.value === browserLang
    ) || { value: "en", label: "English" };
    // Set initial language in form data if not already set
    if (!data.language) {
        onChange({ target: { name: "language", value: defaultLang.value } });
    }
    setSelectedLanguage(defaultLang); // Keep local state for display if needed
  }, [data.language, onChange]); // Depend on data.language and onChange


  // Effect to set initial form visibility based on default selected license ('pro')
  useEffect(() => {
    // Use the passed-in prop for the initial check
    setShowForm(proLicenseCount > 0);
    setUsingFreeTrial(false); // Ensure free trial isn't active initially
    // Set initial type in form data based on default state
    onChange({ target: { name: "jobOfferType", value: "pro" } });
    onChange({ target: { name: "type", value: "pro" } });
  }, [proLicenseCount, onChange]); // Depend on initial license count and onChange

  // Tooltip handlers
  const handleMouseEnter = (tooltipId) => {
    setShowTooltip(tooltipId);
  };

  const handleMouseLeave = () => {
    setShowTooltip(null);
  };

  // Updated to handle all 4 license types using props
  const handleJobOfferTypeChange = (event) => {
    const selectedType = event.target.value;
    setJobOfferType(selectedType);
    let shouldShowForm = false;

    // Check license availability using props
    if (selectedType === "basic") {
        shouldShowForm = basicLicenseCount > 0;
    } else if (selectedType === "growth") {
        shouldShowForm = growthLicenseCount > 0;
    } else if (selectedType === "pro") { // Keep 'pro' for Professional
        shouldShowForm = proLicenseCount > 0;
    } else if (selectedType === "premium") {
        shouldShowForm = premiumLicenseCount > 0;
    }

    setShowForm(shouldShowForm);
    setUsingFreeTrial(false); // Reset free trial state when changing type
    // Update form data state via parent's onChange handler
    onChange({ target: { name: "jobOfferType", value: selectedType } });
    onChange({ target: { name: "type", value: selectedType } });
  };

  // Transforms assessment data for react-select
  function transformArray(inputArray = []) { // Add default empty array
    try {
      return inputArray.map((item) => ({
        value: item.id,
        label: item.title,
      }));
    } catch (error) {
      console.error("Error transforming assessments array:", error);
      return []; // Return empty array on error
    }
  }

  // Updated helper to check license availability using props
  const hasLicense = (type) => {
    if (type === "basic") return basicLicenseCount > 0;
    if (type === "growth") return growthLicenseCount > 0;
    if (type === "pro") return proLicenseCount > 0;   // Keep 'pro' for Professional
    if (type === "premium") return premiumLicenseCount > 0;
    return false;
  };

  // Simplified free trial check using the prop
  const canUseFreeTrial = () => {
    // Check if the trial hasn't been used (passed via prop)
    // AND implicitly, if there is a concept of a free trial available (usually 1)
    // Assuming the presence of the prop means a trial is potentially available
    return !hasUsedFreeTrial;
  };

  const handleFreeTrialClick = () => {
    // Logic to use the free trial would typically happen in the parent onSubmit
    // Here, we just update the local state and form data
    setJobOfferType("Free"); // Use "Free" as the type indicator
    setUsingFreeTrial(true);
    setShowForm(true); // Show the form when using free trial
    // Update form data state via parent's onChange handler
    onChange({ target: { name: "jobOfferType", value: "Free" } });
    onChange({ target: { name: "type", value: "Free" } });
  };

  // Determine if assessments should be disabled based on selected plan
  // Assessments are available from Professional onwards (Pro, Premium)
  const assessmentsDisabled = ["basic", "growth", "Free"].includes(jobOfferType);


  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("createNewJobPosting")}>
      <form onSubmit={onSubmit} className="space-y-4">
        {/* License Type Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-600 leading-relaxed mb-2">{t("selectLicenseType")}</label> {/* Added a main label */}
          <div className="flex flex-wrap gap-x-4 gap-y-2"> {/* Use flex-wrap for responsiveness */}
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio"
                name="jobOfferType"
                value="basic"
                checked={jobOfferType === "basic"}
                onChange={handleJobOfferTypeChange}
              />
              <span className="ml-2">{t("basicLicense", { count: basicLicenseCount })}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio"
                name="jobOfferType"
                value="growth"
                checked={jobOfferType === "growth"}
                onChange={handleJobOfferTypeChange}
              />
              <span className="ml-2">{t("growthLicense", { count: growthLicenseCount })}</span>
              {/* You need to add 'growthLicense' to your translation files */}
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio"
                name="jobOfferType"
                value="pro" // Keep 'pro' for Professional
                checked={jobOfferType === "pro"}
                onChange={handleJobOfferTypeChange}
              />
              {/* Update translation key if needed */}
              <span className="ml-2">{t("professionalLicense", { count: proLicenseCount })} (★ {t("mostPopular")})</span>
              {/* You need to add 'professionalLicense' and 'mostPopular' to your translation files */}
            </label>
             <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio"
                name="jobOfferType"
                value="premium"
                checked={jobOfferType === "premium"}
                onChange={handleJobOfferTypeChange}
              />
              <span className="ml-2">{t("premiumLicense", { count: premiumLicenseCount })}</span>
              {/* You need to add 'premiumLicense' to your translation files */}
            </label>
          </div>
          <p className="mt-3 text-sm"> {/* Adjusted margin */}
            <a href="/checkout" className="text-blue-600 underline hover:text-blue-800">
              {t("acquireMoreLicenses")}
            </a>
          </p>
        </div>

        {/* Free Trial Option */}
        {canUseFreeTrial() && !usingFreeTrial && (
          <div className="mt-4 p-4 bg-yellow-100 text-yellow-800 rounded border border-yellow-300">
             <p className="font-medium">{t("freeTrialAvailable")}</p>
             <p className="text-sm mt-1">{t("freeTrialDescription")}</p> {/* Add a description key */}
             <button
               type="button" // Prevent form submission
               onClick={handleFreeTrialClick}
               className="bg-blue-500 text-white px-3 py-1 rounded-md mt-2 text-sm hover:bg-[#A47713] hover:bg-[#8a6310] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
             >
               {t("yesUseFreeTrial")}
             </button>
           </div>
        )}

        {/* No License Warning */}
        {!hasLicense(jobOfferType) && !usingFreeTrial && (
          <div className="mt-4 p-4 bg-red-100 text-red-800 rounded border border-red-300">
            <p>
              <Trans i18nKey="noLicenseSelected" t={t}>
                {/* Default fallback text, keys will be used from translation file */}
                You do not have any <strong>{{ licenseType: t(jobOfferType+'LicenseName', jobOfferType) }}</strong> licenses available. Please {/* Need jobOfferType+'LicenseName' keys like basicLicenseName, etc. */}
                <a href="/checkout" className="text-blue-600 underline hover:text-blue-800">
                  purchase a license
                </a>{" "}
                or select a different license type.
              </Trans>
            </p>
          </div>
        )}

        {/* Job Details Form - Shown only if a valid license is selected/available or free trial is used */}
        {showForm && (
          <>
            {/* Language Selection */}
             <div>
               <label htmlFor="language-select" className="block text-sm font-medium text-slate-600 leading-relaxed">
                 {t("selectLanguage")}{" "}
                 <span className="text-red-600 text-xs font-normal">({t("important")})</span>
               </label>
               <select
                 id="language-select"
                 name="language"
                 required
                 value={data.language || ''} // Controlled component
                 onChange={onChange}
                 className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
               >
                 {supportedLanguages.map(lang => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                 ))}
               </select>
               <p className="mt-1 text-xs text-slate-500">{t("languageSelectionTooltip")}</p> {/* Add translation */}
             </div>

            {/* Job Title */}
            <div>
              <label htmlFor="job-title" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("title")}{" "}
                <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <input
                id="job-title"
                name="Titre" // Keep original name attribute if backend expects it
                type="text"
                required
                value={data.Titre || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("titlePlaceholder")} // Add placeholder translation
              />
            </div>

             {/* Company Name */}
             <div>
               <label htmlFor="company-name" className="block text-sm font-medium text-slate-600 leading-relaxed">
                 {t("companyName")}{" "}
                 <span className="text-red-600 text-xs font-normal">({t("important")})</span>
               </label>
               <input
                 id="company-name"
                 name="Entreprise"
                 type="text"
                 required // Make company name required? Adjust as needed.
                 value={data.Entreprise || ""}
                 onChange={onChange}
                 className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                 placeholder={t("companyNamePlaceholder")} // Add placeholder translation
               />
             </div>

             {/* Company Description */}
             <div>
               <label htmlFor="company-description" className="block text-sm font-medium text-slate-600 leading-relaxed">
                 {t("companyDescription")}
               </label>
               <textarea
                 id="company-description"
                 name="DescriptionDeLentreprise"
                 rows="3" // Adjust rows as needed
                 value={data.DescriptionDeLentreprise || ""}
                 onChange={onChange}
                 className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                 placeholder={t("companyDescriptionPlaceholder")} // Add placeholder translation
               />
             </div>

             {/* Location */}
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("location")}{" "}
                <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <input
                id="location"
                name="Lieu"
                type="text"
                required // Make location required? Adjust as needed.
                value={data.Lieu || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("locationPlaceholder", "e.g., City, Country")} // Add placeholder translation with example
              />
              <p className="mt-1 text-xs text-slate-500">{t("cityCountryFormat")}</p> {/* Add translation */}
            </div>

            {/* Publication Date */}
            <div>
              <label htmlFor="publication-date" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("publicationDate")}{" "}
                <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <input
                id="publication-date"
                name="DateDePublication"
                type="date"
                required
                value={data.DateDePublication || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            {/* HR Contact Name */}
            <div>
              <label htmlFor="hr-contact-name" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("hrContactName")}{" "}
                 <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <input
                id="hr-contact-name"
                name="NomDuContactRH"
                type="text"
                required // Make HR contact required? Adjust as needed.
                value={data.NomDuContactRH || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            {/* HR Email */}
            <div>
              <label htmlFor="hr-email" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("hrEmail")}{" "}
                <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <input
                id="hr-email"
                name="Email"
                type="email"
                required // Make email required?
                value={data.Email || ""}
                onChange={onChange}
                 className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="contact@example.com"
              />
            </div>

            {/* HR Phone */}
            <div>
              <label htmlFor="hr-phone" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("hrPhone")}
              </label>
              <input
                id="hr-phone"
                name="Telephone"
                type="tel"
                value={data.Telephone || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("hrPhonePlaceholder")} // Add translation
              />
            </div>

             {/* Job Type */}
             <div>
               <label htmlFor="job-type" className="block text-sm font-medium text-slate-600 leading-relaxed">
                 {t("jobType")}{" "}
                 <span className="text-red-600 text-xs font-normal">({t("important")})</span>
               </label>
               <select
                 id="job-type"
                 name="TypeDePoste"
                 required
                 value={data.TypeDePoste || ""}
                 onChange={onChange}
                 className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
               >
                 <option value="">{t("selectJobType")}</option>
                 <option value="Temps plein">{t("fullTime")}</option>
                 <option value="Temps partiel">{t("partTime")}</option>
                 <option value="Contrat">{t("contract")}</option>
                 <option value="Stage">{t("internship")}</option>
                 <option value="Freelance">{t("freelance")}</option>
                 {/* Add other types if needed */}
               </select>
             </div>

            {/* Sector */}
            <div>
              <label htmlFor="sector" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("sector")}
              </label>
              <input
                id="sector"
                name="Secteur"
                type="text"
                value={data.Secteur || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("sectorPlaceholder")} // Add translation
              />
            </div>

            {/* Application Deadline */}
            <div>
              <label htmlFor="application-deadline" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("applicationDeadline")}{" "}
                <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <input
                id="application-deadline"
                name="DateLimiteDeCandidature"
                type="date"
                required
                value={data.DateLimiteDeCandidature || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            {/* Role Category */}
            <div>
              <label htmlFor="role-category" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("roleCategory")}{" "}
                 <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <input
                id="role-category"
                name="CategorieDeRole"
                type="text"
                required
                value={data.CategorieDeRole || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("roleCategoryPlaceholder")} // Add translation
              />
            </div>

            {/* Experience Level */}
            <div>
              <label htmlFor="experience-level" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("experienceLevel")}{" "}
                 <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <select
                id="experience-level"
                name="NiveauDExperience"
                required
                value={data.NiveauDExperience || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="">{t("selectExperienceLevel")}</option>
                <option value="Stage">{t("Intern")}</option>
                <option value="Debutant">{t("Entry")}</option>
                <option value="Mid">{t("Mid")}</option>
                <option value="Senior">{t("Senior")}</option>
                <option value="Manager">{t("Manager")}</option>
                <option value="Directeur">{t("Director")}</option>
                <option value="Executif">{t("Executive")}</option> {/* Corrected typo */}
              </select>
            </div>

            {/* Job Description */}
            <div>
              <label htmlFor="job-description" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("jobDescription")}{" "}
                 <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <textarea
                id="job-description"
                name="Description"
                rows="5" // Adjust rows
                required
                value={data.Description || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("jobDescriptionPlaceholder")} // Add translation
              />
            </div>

            {/* Minimum Experience */}
            <div>
              <label htmlFor="min-experience" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("minimumExperience")}{" "}
                <span className="text-red-600 text-xs font-normal">({t("important")})</span>
              </label>
              <input
                id="min-experience"
                name="minimumRelevantYearsOfExperience"
                type="number"
                required
                min="0" // Add min attribute
                value={data.minimumRelevantYearsOfExperience || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("minExperiencePlaceholder", "e.g., 3")} // Add translation
              />
            </div>

            {/* Qualifications */}
             <div>
               <label htmlFor="qualifications" className="block text-sm font-medium text-slate-600 leading-relaxed">
                 {t("qualifications")}{" "}
                 <span className="text-red-600 text-xs font-normal">({t("important")})</span>
               </label>
               <textarea
                 id="qualifications"
                 name="Qualifications"
                 rows="4" // Adjust rows
                 required // Make qualifications required?
                 value={data.Qualifications || ""}
                 onChange={onChange}
                 className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                 placeholder={t("qualificationsPlaceholder")} // Add translation
               />
             </div>

            {/* Responsibilities */}
            <div>
              <label htmlFor="responsibilities" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("responsibilities")}
              </label>
              <textarea
                id="responsibilities"
                name="Responsibilities"
                rows="4" // Adjust rows
                value={data.Responsibilities || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("responsibilitiesPlaceholder")} // Add translation
              />
            </div>

            {/* Additional Information */}
            <div>
              <label htmlFor="additional-info" className="block text-sm font-medium text-slate-600 leading-relaxed">
                {t("additionalInformation")}
              </label>
              <textarea
                id="additional-info"
                name="additionalInformation"
                rows="3" // Adjust rows
                value={data.additionalInformation || ""}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t("additionalInformationPlaceholder")} // Add translation
              />
            </div>

             {/* Assessments Section */}
             <div>
               <div className="flex items-center mb-1">
                 <label htmlFor="assessments-select" className="block text-sm font-medium text-slate-600 leading-relaxed mr-1">
                   {t("assessmentTests")}
                 </label>
                 <span className="relative inline-flex items-center">
                   <FontAwesomeIcon
                     icon={faQuestionCircle}
                     className="text-gray-400 cursor-pointer hover:text-slate-600 leading-relaxed"
                     onMouseEnter={() => handleMouseEnter("assessments")}
                     onMouseLeave={handleMouseLeave}
                     aria-describedby="assessments-tooltip" // Accessibility
                   />
                   {showTooltip === "assessments" && (
                     <div
                       id="assessments-tooltip" // Accessibility
                       className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-gray-800 text-white text-xs p-3 rounded-md shadow-lg z-10 whitespace-normal"
                       role="tooltip"
                     >
                       {t("assessmentsTooltip")}
                     </div>
                   )}
                 </span>
               </div>
               <div>
                 {assessmentsDisabled ? (
                   <p className="text-sm text-slate-500 mt-1 bg-gray-100 p-2 rounded">
                       {t("assessmentTestsProfessionalPremiumOnly")}
                       {/* Update translation key */}
                   </p>
                 ) : (
                   <Select
                     id="assessments-select"
                     isMulti
                     options={transformArray(assessments)}
                     name="assessments" // Ensure onSubmit handles this correctly (might need custom handling)
                     // The value and onChange for react-select often need specific handling.
                     // Assuming parent component manages 'data.assessments' in the correct format [{value, label}, ...]
                     // and onChange handles the update.
                     value={data.assessments || []}
                     onChange={(selectedOptions) => onChange({ target: { name: 'assessments', value: selectedOptions || [] }})} // Basic handling example
                     className="mt-1 react-select-container"
                     classNamePrefix="react-select"
                     placeholder={t("selectAssessmentsPlaceholder")}
                     noOptionsMessage={() => t('noAssessmentsAvailable')} // Add translation
                   />
                 )}
               </div>
             </div>

            {/* Submit Button */}
            <div className="pt-4"> {/* Add padding top */}
                <button
                    type="submit"
                    disabled={disableButtons}
                    className={`w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${disableButtons ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {disableButtons ? t("submitting") : t("submitJobPosting")} {/* Update translation */}
                </button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

// Updated PropTypes
CreateJobModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired, // Contains the form data
  disableButtons: PropTypes.bool,
  assessments: PropTypes.arrayOf(PropTypes.shape({ // More specific assessment shape
      id: PropTypes.any.isRequired,
      title: PropTypes.string.isRequired,
      // Add other expected assessment properties if needed
  })),
  // License counts passed from parent
  basicLicenseCount: PropTypes.number.isRequired,
  growthLicenseCount: PropTypes.number.isRequired,
  proLicenseCount: PropTypes.number.isRequired, // For Professional
  premiumLicenseCount: PropTypes.number.isRequired,
  // Trial status passed from parent
  hasUsedFreeTrial: PropTypes.bool.isRequired,
};

// Updated Default Props
CreateJobModal.defaultProps = {
  disableButtons: false,
  assessments: [], // Default to empty array
  // Set default counts to 0 if not provided by parent
  basicLicenseCount: 0,
  growthLicenseCount: 0,
  proLicenseCount: 0,
  premiumLicenseCount: 0,
  hasUsedFreeTrial: true, // Default assuming trial is used or unavailable if prop missing
};

export default CreateJobModal;